import { useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Pencil, Plus, Target, Trash2 } from 'lucide-react';
import { PermissionButton } from '@/client/components/admin/PermissionButton';
import { PERMISSIONS } from '@/client/config/permissions';
import { useGate } from '@/client/hooks/useGate';
import { useDeleteGoalMutation, usePerfGoalsQuery, useReviewCyclesQuery, useUpsertGoalMutation } from '@/client/api/hr/perf';
import { GOAL_STATUS_BADGE, GOAL_STATUS_LABEL, type Goal, type GoalStatus } from '@/client/types/hr';
import { HrPageShell } from './components/HrPageShell';
import { DeleteConfirmDialog, EmployeeAvatar, EmployeeLink, EmptyState, ProgressBar, RowMenu, StatusBadge } from './components/hr-ui';
import { GoalDialog } from './components/GoalDialog';
import { formatDay } from './components/leave-format';
import { useEmployees } from './hr-store';

const filterClass = 'h-9 rounded-md border border-gray-200 bg-white px-3 text-sm outline-none ring-blue-500 focus:ring-2';

const GoalsPage = () => {
  const goalsQ = usePerfGoalsQuery();
  const cycles = useReviewCyclesQuery().data ?? [];
  const employees = useEmployees();
  const upsert = useUpsertGoalMutation();
  const remove = useDeleteGoalMutation();
  const canManage = useGate(PERMISSIONS.perf.manage).allowed;
  const [status, setStatus] = useState<GoalStatus | 'all'>('all');
  const [employeeId, setEmployeeId] = useState('all');
  const [editing, setEditing] = useState<Goal | null | 'new'>(null);
  const [deleting, setDeleting] = useState<Goal | null>(null);

  const goals = goalsQ.data ?? [];
  const byId = useMemo(() => new Map(employees.map((e) => [e.id, e])), [employees]);
  const filtered = goals.filter((g) => (status === 'all' || g.status === status) && (employeeId === 'all' || g.employeeId === employeeId));
  const today = new Date().toISOString().slice(0, 10);

  const stats = [
    { key: 'total', label: 'Goals', value: goals.length },
    { key: 'progress', label: 'In progress', value: goals.filter((g) => g.status === 'in_progress').length },
    { key: 'risk', label: 'At risk', value: goals.filter((g) => g.status === 'at_risk').length },
    { key: 'overdue', label: 'Overdue', value: goals.filter((g) => g.status !== 'completed' && g.dueDate && g.dueDate < today).length },
  ];

  const confirmDelete = () => {
    if (!deleting) return;
    const g = deleting;
    remove.mutate(g.id, { onSuccess: () => toast.success('Goal deleted'), onError: (e) => toast.error((e as Error).message) });
    setDeleting(null);
  };

  return (
    <HrPageShell
      mock={false}
      title="Goals"
      description="What everyone is working towards. People set and update their own in My workplace."
      crumbs={[{ label: 'Human Resource', href: '/admin/human-resource' }, { label: 'Performance' }, { label: 'Goals' }]}
      icon={Target}
      actions={
        <PermissionButton code={PERMISSIONS.perf.manage} className="gap-2" onClick={() => setEditing('new')}>
          <Plus className="h-4 w-4" /> Add goal for someone
        </PermissionButton>
      }
      stats={stats}
    >
      <div className="mb-4 flex flex-wrap gap-3">
        <select value={employeeId} onChange={(e) => setEmployeeId(e.target.value)} className={filterClass}>
          <option value="all">Everyone</option>
          {employees.map((e) => (
            <option key={e.id} value={e.id}>
              {e.name}
            </option>
          ))}
        </select>
        <select value={status} onChange={(e) => setStatus(e.target.value as GoalStatus | 'all')} className={filterClass}>
          <option value="all">All statuses</option>
          {Object.entries(GOAL_STATUS_LABEL).map(([v, l]) => (
            <option key={v} value={v}>
              {l}
            </option>
          ))}
        </select>
      </div>

      {goalsQ.isError ? (
        <EmptyState label={`Could not load goals: ${(goalsQ.error as Error)?.message ?? 'unknown error'}`} />
      ) : goalsQ.isLoading ? (
        <EmptyState label="Loading goals…" />
      ) : filtered.length === 0 ? (
        <EmptyState label={goals.length ? 'No goals match these filters.' : 'No goals yet. People add theirs in My workplace.'} />
      ) : (
        <div className="overflow-hidden rounded-xl border border-gray-100 bg-white shadow-sm">
          <table className="w-full text-left text-sm">
            <thead className="border-b border-gray-100 bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
              <tr>
                <th className="px-4 py-3 font-medium">Goal</th>
                <th className="px-4 py-3 font-medium">Owner</th>
                <th className="px-4 py-3 font-medium">Cycle</th>
                <th className="px-4 py-3 font-medium">Due</th>
                <th className="px-4 py-3 font-medium">Progress</th>
                <th className="px-4 py-3 font-medium">Status</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filtered.map((g) => {
                const e = byId.get(g.employeeId);
                const overdue = g.status !== 'completed' && g.dueDate && g.dueDate < today;
                return (
                  <tr key={g.id} className={canManage ? 'cursor-pointer hover:bg-gray-50' : ''} onClick={() => canManage && setEditing(g)}>
                    <td className="px-4 py-3">
                      <p className="font-medium text-gray-900">{g.title}</p>
                      {g.description && <p className="line-clamp-1 text-xs text-gray-500">{g.description}</p>}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <EmployeeAvatar employee={e} size="sm" />
                        <EmployeeLink employee={e} />
                      </div>
                    </td>
                    <td className="px-4 py-3 text-gray-600">{cycles.find((c) => c.id === g.cycleId)?.name ?? '—'}</td>
                    <td className={`px-4 py-3 ${overdue ? 'text-red-600' : 'text-gray-600'}`}>{g.dueDate ? formatDay(g.dueDate) : '—'}</td>
                    <td className="px-4 py-3">
                      <p className="mb-1 text-xs text-gray-500">{g.progress}%</p>
                      <ProgressBar value={g.progress} className="w-24" />
                    </td>
                    <td className="px-4 py-3">
                      <StatusBadge label={GOAL_STATUS_LABEL[g.status]} className={GOAL_STATUS_BADGE[g.status]} />
                    </td>
                    <td className="px-4 py-3 text-right">
                      <RowMenu
                        items={[
                          { label: 'Edit', icon: Pencil, hidden: !canManage, onSelect: () => setEditing(g) },
                          { label: 'Delete', icon: Trash2, danger: true, hidden: !canManage, onSelect: () => setDeleting(g) },
                        ]}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <GoalDialog
        open={editing !== null}
        goal={editing === 'new' ? null : editing}
        cycles={cycles}
        employees={employees}
        busy={upsert.isPending}
        onClose={() => setEditing(null)}
        onSave={(body) =>
          upsert.mutate(body, {
            onSuccess: () => {
              toast.success(body.id ? 'Goal updated' : 'Goal added');
              setEditing(null);
            },
            onError: (e) => toast.error((e as Error).message || 'Could not save the goal'),
          })
        }
      />
      <DeleteConfirmDialog
        open={!!deleting}
        onOpenChange={(v) => !v && setDeleting(null)}
        title="Delete goal?"
        description={`"${deleting?.title}" will be deleted for good. To stop tracking it but keep the record, mark it completed instead.`}
        onConfirm={confirmDelete}
      />
    </HrPageShell>
  );
};

export default GoalsPage;
